import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const TOKEN_KEY = "kostra:token";

export const api = axios.create({ baseURL: API });

// Inject Authorization header from localStorage on every request.
api.interceptors.request.use((config) => {
  try {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${t}`;
    }
  } catch {}
  return config;
});

// On 401, drop the bad token and trigger the global login modal (if mounted).
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401) {
      try { localStorage.removeItem(TOKEN_KEY); } catch {}
      // Skip auto-prompt on the /auth/me bootstrap probe.
      if (!url.includes("/auth/me") && typeof window !== "undefined" && window.__kostraOpenLogin) {
        window.__kostraOpenLogin();
      }
    }
    return Promise.reject(err);
  }
);

export const fmtINR = (n) => {
  const v = Number.isFinite(+n) ? +n : 0;
  return `₹ ${v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtPct = (n) => `${(Number.isFinite(+n) ? +n : 0).toFixed(2)}%`;

export const calcLine = (qty, unit, ratePerKg) => {
  const q = +qty || 0;
  const r = +ratePerKg || 0;
  const qtyKg = (unit || "g").toLowerCase().startsWith("k") ? q : q / 1000;
  return Math.round(qtyKg * r * 100) / 100;
};

export const computeSummary = (recipe) => {
  const sumLines = (arr) =>
    (arr || []).reduce((s, i) => s + calcLine(i.quantity, i.unit, i.rate_per_kg), 0);
  const primary_total = +sumLines(recipe.primary_ingredients).toFixed(2);
  const secondary_total = +sumLines(recipe.secondary_ingredients).toFixed(2);
  const raw_total = +(primary_total + secondary_total).toFixed(2);

  const u = recipe.utilities || {};
  const o = recipe.overheads || {};
  const variable_total = +(
    (+u.gas || 0) + (+u.electricity || 0) + (+u.water || 0) +
    (+u.packaging || 0) + (+u.delivery || 0)
  ).toFixed(2);
  const fixed_total = +(
    (+o.labour || 0) + (+o.rent || 0) + (+o.kitchen_overhead || 0) +
    (+o.maintenance || 0) + (+o.misc || 0)
  ).toFixed(2);

  const total_recipe_cost = +(raw_total + variable_total + fixed_total).toFixed(2);
  const num_portions = Math.max(1, parseInt(recipe.num_portions || 1, 10));
  const cost_per_portion = +(total_recipe_cost / num_portions).toFixed(2);

  let pct = +recipe.desired_food_cost_pct || 30;
  pct = Math.min(90, Math.max(5, pct));
  const suggested_price = cost_per_portion ? +(cost_per_portion / (pct / 100)).toFixed(2) : 0;
  const min_price = cost_per_portion ? +(cost_per_portion / 0.35).toFixed(2) : 0;
  const recommended_price = cost_per_portion ? +(cost_per_portion / 0.30).toFixed(2) : 0;
  const premium_price = cost_per_portion ? +(cost_per_portion / 0.25).toFixed(2) : 0;
  const profit_per_portion = +(suggested_price - cost_per_portion).toFixed(2);
  const gross_margin_pct = suggested_price ? +((profit_per_portion / suggested_price) * 100).toFixed(2) : 0;

  const net_weight_per_portion_g = +recipe.net_weight_per_portion_g || 0;
  const total_net_weight_g = +(net_weight_per_portion_g * num_portions).toFixed(3);
  const cost_per_kg = net_weight_per_portion_g > 0
    ? +((cost_per_portion / net_weight_per_portion_g) * 1000).toFixed(2)
    : 0;
  const cost_per_gram = net_weight_per_portion_g > 0
    ? +(cost_per_portion / net_weight_per_portion_g).toFixed(4)
    : 0;

  return {
    primary_total, secondary_total, raw_total,
    variable_total, fixed_total, total_recipe_cost,
    cost_per_portion, desired_food_cost_pct: pct, suggested_price,
    min_price, recommended_price, premium_price,
    profit_per_portion, gross_margin_pct,
    net_weight_per_portion_g, total_net_weight_g, cost_per_kg, cost_per_gram,
  };
};
